import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CircleNotch,
  CheckSquare,
  Square,
  MapPin,
  Clock,
  CalendarBlank,
  Play,
  CheckCircle,
  ArrowRight,
  Lightning,
  ClipboardText
} from '@phosphor-icons/react';


const STATUS_STYLES = {
  'scheduled': 'bg-blue-100 text-blue-700',
  'in_progress': 'bg-yellow-100 text-yellow-700',
  'completed': 'bg-green-100 text-green-700',
};
const STATUS_LABELS = {
  'scheduled': 'Programada',
  'in_progress': 'En Progreso',
  'completed': 'Completada',
};
const PRIORITY_COLORS = {
  'low': 'border-l-slate-400',
  'normal': 'border-l-blue-500',
  'high': 'border-l-orange-500',
  'urgent': 'border-l-red-500',
};

function WOCard({ wo, onToggleChecklist, onStartWork }) {
  const [expanded, setExpanded] = useState(false);
  const completedTasks = wo.checklist?.filter(t => t.completed).length || 0;
  const totalTasks = wo.checklist?.length || 0;

  return (
    <div className={`bg-white rounded-xl border border-slate-200 border-l-4 ${PRIORITY_COLORS[wo.priority] || 'border-l-blue-500'} overflow-hidden`} data-testid={`wo-card-${wo.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-slate-500">{wo.work_order_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[wo.status]}`}>
                {STATUS_LABELS[wo.status]}
              </span>
            </div>
            <h3 className="font-semibold text-slate-900 mt-1 text-sm sm:text-base">{wo.title}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{wo.client_name}</p>
          </div>
          {wo.status === 'scheduled' && (
            <button
              onClick={() => onStartWork(wo.id)}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-xs font-semibold hover:bg-yellow-600 transition-colors"
              data-testid={`start-work-${wo.id}`}
            >
              <Play size={14} weight="fill" /> Iniciar
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <CalendarBlank size={14} />
            {wo.scheduled_date} {wo.scheduled_time && `- ${wo.scheduled_time}`}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {wo.estimated_hours || '?'} hrs
          </span>
          {wo.project_address && (
            <span className="flex items-center gap-1">
              <MapPin size={14} />
              {wo.project_address}{wo.project_city ? `, ${wo.project_city}` : ''}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {totalTasks > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">Progreso</span>
              <span className="font-semibold text-slate-700">{completedTasks}/{totalTasks}</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-300"
                style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 text-xs text-blue-600 font-medium hover:text-blue-700"
        >
          {expanded ? 'Ocultar checklist' : `Ver checklist (${totalTasks} tareas)`}
        </button>
      </div>

      {/* Expanded checklist */}
      {expanded && wo.checklist?.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-2 bg-slate-50/50">
          {wo.checklist.map((task) => (
            <button
              key={task.id}
              onClick={() => wo.status === 'in_progress' && onToggleChecklist(wo.id, task.id)}
              className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left text-sm transition-colors ${
                task.completed ? 'bg-green-50' : 'bg-white'
              } ${wo.status === 'in_progress' ? 'hover:bg-slate-100 active:bg-slate-200 cursor-pointer' : ''}`}
              disabled={wo.status !== 'in_progress'}
              data-testid={`checklist-${task.id}`}
            >
              {task.completed ? (
                <CheckSquare size={20} className="text-green-600 shrink-0" weight="fill" />
              ) : (
                <Square size={20} className="text-slate-400 shrink-0" />
              )}
              <span className={task.completed ? 'line-through text-slate-400' : 'text-slate-700'}>
                {task.description}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Footer link */}
      <Link
        to={`/work-orders/${wo.id}`}
        className="block px-4 py-2.5 bg-slate-50 text-xs font-medium text-blue-600 hover:bg-slate-100 transition-colors flex items-center justify-between"
      >
        Ver detalles completos
        <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function TechDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/tech-dashboard`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      console.error('Error fetching tech dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChecklist = async (woId, taskId) => {
    try {
      await axios.post(`${API_URL}/api/work-orders/${woId}/checklist/${taskId}/toggle`, {}, { withCredentials: true });
      fetchDashboard();
    } catch (error) {
      console.error('Error toggling checklist:', error);
    }
  };

  const handleStartWork = async (woId) => {
    try {
      await axios.put(`${API_URL}/api/work-orders/${woId}`, { status: 'in_progress' }, { withCredentials: true });
      fetchDashboard();
    } catch (error) {
      console.error('Error starting work:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-6 animate-fadeIn max-w-2xl mx-auto" data-testid="tech-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Mi Panel de Trabajo</h1>
        <p className="text-sm text-slate-500 mt-1">
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600" data-testid="stat-today">{stats.today_count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Hoy</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600" data-testid="stat-in-progress">{stats.in_progress_count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">En Progreso</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-700" data-testid="stat-upcoming">{stats.upcoming_count || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Proximas</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600" data-testid="stat-completed">{stats.completed_total || 0}</p>
          <p className="text-xs text-slate-500 mt-1">Completadas</p>
        </div>
      </div>

      {/* In Progress */}
      {data?.in_progress?.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 mb-3">
            <Lightning size={20} className="text-yellow-500" weight="fill" />
            En Progreso
          </h2>
          <div className="space-y-3">
            {data.in_progress.map(wo => (
              <WOCard key={wo.id} wo={wo} onToggleChecklist={handleToggleChecklist} onStartWork={handleStartWork} />
            ))}
          </div>
        </div>
      )}

      {/* Today */}
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 mb-3">
          <CalendarBlank size={20} className="text-blue-600" />
          Ordenes de Hoy ({data?.today?.length || 0})
        </h2>
        {data?.today?.length > 0 ? (
          <div className="space-y-3">
            {data.today.map(wo => (
              <WOCard key={wo.id} wo={wo} onToggleChecklist={handleToggleChecklist} onStartWork={handleStartWork} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <CheckCircle size={40} className="text-green-500 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No hay ordenes programadas para hoy</p>
          </div>
        )}
      </div>

      {/* Upcoming */}
      {data?.upcoming?.length > 0 && (
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 mb-3">
            <ClipboardText size={20} className="text-slate-600" />
            Proximas (7 dias)
          </h2>
          <div className="space-y-3">
            {data.upcoming.map(wo => (
              <WOCard key={wo.id} wo={wo} onToggleChecklist={handleToggleChecklist} onStartWork={handleStartWork} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
